
import React from 'react';
import { Shield, CheckCircle, Users, Lock, Eye, UserCheck, Briefcase, Globe, Heart } from 'lucide-react';
import { useLanguage } from '@/hooks/useLanguage';
import { IS_PRODUCTION_READY } from '@/utils/env';
import { AppStoreDownloadDialog } from '@/components/common/AppStoreDownloadDialog';

export const SafetySection = () => {
  const { t } = useLanguage();
  const [showDialog, setShowDialog] = React.useState(false);
  const safetyFeatures = [
    {
      icon: UserCheck,
      title: t('idVerificationTitle', 'safety'),
      description: t('idVerificationDesc', 'safety'),
      stats: t('idVerificationStats', 'safety'),
      gradient: 'from-blue-500/20 to-indigo-600/20'
    },
    {
      icon: Briefcase,
      title: t('professionalTitle', 'safety'),
      description: t('professionalDesc', 'safety'),
      stats: t('professionalStats', 'safety'),
      gradient: 'from-purple-500/20 to-violet-600/20'
    },
    {
      icon: Globe,
      title: t('cultureTitle', 'safety'),
      description: t('cultureDesc', 'safety'),
      stats: t('cultureStats', 'safety'),
      gradient: 'from-green-500/20 to-emerald-600/20'
    },
    {
      icon: Heart,
      title: t('interestsTitle', 'safety'),
      description: t('interestsDesc', 'safety'),
      stats: t('interestsStats', 'safety'),
      gradient: 'from-pink-500/20 to-rose-600/20'
    },
    {
      icon: Eye,
      title: t('trackingTitle', 'safety'),
      description: t('trackingDesc', 'safety'),
      stats: t('trackingStats', 'safety'),
      gradient: 'from-orange-500/20 to-red-600/20'
    },
    {
      icon: Lock,
      title: t('secureCommTitle', 'safety'),
      description: t('secureCommDesc', 'safety'),
      stats: t('secureCommStats', 'safety'),
      gradient: 'from-cyan-500/20 to-blue-600/20'
    }
  ];

  const trustMetrics = [
    { label: t('backgroundVerified', 'safety'), value: '100%', icon: Shield },
    { label: t('trustScoreAverage', 'safety'), value: '4.9/5', icon: Users },
    { label: t('emergencyResponse', 'safety'), value: '<2min', icon: Eye }
  ];

  return (
    <section id="safety" className="relative pt-8 pb-12 bg-gradient-to-br from-gray-50 via-white to-slate-50 overflow-hidden">
      {/* Animated background */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-1/4 w-96 h-96 bg-gradient-to-br from-[#22C55E]/8 to-transparent rounded-full blur-3xl animate-float-slow"></div>
        <div className="absolute bottom-20 right-1/4 w-80 h-80 bg-gradient-to-br from-blue-400/8 to-transparent rounded-full blur-2xl animate-float-reverse"></div>
        
        {/* Moving security icons */}
        <div className="absolute inset-0 overflow-hidden opacity-5">
          <Shield className="absolute top-1/4 left-10 w-16 h-16 text-[#22C55E] animate-bounce-slow" />
          <Lock className="absolute top-1/3 right-20 w-12 h-12 text-blue-500 animate-bounce-delayed" />
          <UserCheck className="absolute bottom-1/3 left-1/3 w-14 h-14 text-green-500 animate-pulse-slow" />
        </div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-20">
          <div className="inline-flex items-center space-x-2 bg-white/50 backdrop-blur-md border border-white/30 rounded-full px-6 py-3 mb-8 shadow-lg animate-fade-in-up">
            <Shield className="w-5 h-5 text-[#22C55E]" />
            <span className="text-sm font-medium text-[#22C55E]">{t('badge', 'safety')}</span>
          </div>
          
          <h2 className="text-5xl md:text-6xl font-light text-gray-900 mb-6 animate-slide-up">
            {t('sectionTitle', 'safety')}
            <span
              className="block font-medium text-[#22C55E] bg-gradient-to-r from-[#22C55E] to-orange-600 bg-clip-text text-transparent break-words w-full"
              style={{
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
                hyphens: 'auto',
                maxWidth: '100%',
                lineHeight: 1.15,
                padding: '0.25em 0',
                margin: 0,
                display: 'block',
              }}
            >
              {t('sectionTitleHighlight', 'safety')}
            </span>
          </h2>
          
          <p className="text-xl text-gray-600 max-w-3xl mx-auto font-light animate-fade-in-delayed">
            {t('sectionSubtitle', 'safety')}
          </p>
        </div>

        {/* Trust Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-6 mb-20 animate-fade-in-up-delayed">
          {trustMetrics.map((metric, index) => (
            <div 
              key={index}
              className="group bg-white/40 backdrop-blur-xl p-6 rounded-2xl border border-white/30 shadow-lg hover:shadow-xl transition-all duration-500 hover:scale-105 text-center"
            >
              <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-[#22C55E]/10 flex items-center justify-center group-hover:bg-[#22C55E] transition-all duration-500">
                <metric.icon className="w-6 h-6 text-[#22C55E] group-hover:text-white transition-colors duration-500" />
              </div>
              <div className="text-2xl font-semibold text-gray-900 mb-1">{metric.value}</div>
              <div className="text-sm text-gray-600 font-medium">{metric.label}</div>
            </div>
          ))}
        </div>

        {/* Safety Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {safetyFeatures.map((feature, index) => (
            <div 
              key={index}
              className={`group relative bg-white/30 backdrop-blur-xl p-8 rounded-3xl border border-white/40 shadow-xl hover:shadow-2xl transition-all duration-700 hover:scale-105 animate-fade-in-up`}
              style={{ animationDelay: `${index * 0.15}s` }}
            >
              {/* Gradient overlay */}
              <div className={`absolute inset-0 bg-gradient-to-br ${feature.gradient} rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500`}></div>
              
              {/* Content */}
              <div className="relative z-10">
                <div className="w-16 h-16 mb-6 rounded-2xl bg-white/60 backdrop-blur-sm flex items-center justify-center group-hover:bg-[#22C55E] transition-all duration-500 shadow-lg">
                  <feature.icon className="w-8 h-8 text-[#22C55E] group-hover:text-white transition-colors duration-500" />
                </div>
                
                <h3 className="text-xl font-semibold text-gray-900 mb-4 group-hover:text-gray-800 transition-colors duration-300">
                  {feature.title}
                </h3>
                
                <p className="text-gray-600 mb-6 leading-relaxed font-light group-hover:text-gray-700 transition-colors duration-300 text-sm">
                  {feature.description}
                </p>
                
                <div className="bg-white/60 backdrop-blur-sm text-[#22C55E] px-4 py-2 rounded-full inline-block text-xs font-medium shadow-sm group-hover:bg-white/80 transition-all duration-300">
                  {feature.stats}
                </div>
              </div>

              {/* Shine effect */}
              <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700 animate-shimmer"></div>
            </div>
          ))}
        </div>

        {/* Call to Action */}
        <div className="text-center mt-16 animate-fade-in-up-delayed-3">
          <div className="bg-white/30 backdrop-blur-xl border border-white/40 rounded-3xl p-8 shadow-xl max-w-2xl mx-auto">
            <h3 className="text-2xl font-semibold text-gray-900 mb-4">
              {t('ctaTitle', 'safety')}
            </h3>
            <p className="text-gray-600 mb-6 font-light">
              {t('ctaDesc', 'safety')}
            </p>
            <button
              className="bg-gradient-to-r from-[#22C55E] to-orange-600 text-white px-8 py-3 rounded-full font-medium hover:from-[#16A34A] hover:to-orange-700 transition-all duration-300 shadow-lg hover:shadow-xl"
              onClick={IS_PRODUCTION_READY ? () => setShowDialog(true) : undefined}
            >
              {t('ctaButton', 'safety')}
            </button>
          </div>
        </div>
      </div>

      {/* App Store Download Dialog */}
      {IS_PRODUCTION_READY && (
        <AppStoreDownloadDialog
          open={showDialog}
          onOpenChange={setShowDialog}
          title="Download our app to start your verification process"
        />
      )}
    </section>
  );
};
